import { Box, withStyles, Table, formattedCurrency, Icon } from 'vopay';
import type { InterestAccruedDetail, SupportedCurrencies } from 'vopay';
import { FunctionComponent } from 'react';
import styles from './InterestHistoryTable.module.scss';
import { formatDate } from './utils';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type FC = FunctionComponent<InterestHistoryTableProps>;

type InterestHistoryTableProps = {
  id: string;
  interestAccruedDetails: InterestAccruedDetail[];
  hasLongTermAccount: boolean;
  currency: SupportedCurrencies;
  fetching: boolean;
};

const InterestHistoryTable: FC = ({ id, interestAccruedDetails, hasLongTermAccount, fetching, currency }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer.InterestHistoryTab',
  });

  const { t: tDrawer } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer',
  });

  return (
    <div className={cn('interestHistoryTableContainer', 'scrollbar')}>
      <Table
        zebra
        id={id}
        version="2"
        rows={
          fetching
            ? []
            : interestAccruedDetails.map((details) => {
                return {
                  id: details.DateAccrued + details.AmountAccrued + details.Rate + details.InterestMaturityType,
                  data: details,
                };
              })
        }
        columns={[
          {
            id: 'DateAccrued',
            header: t('DateAccrued.column', { defaultValue: 'Date' }),
            align: 'left',
            template: ({ data }) => formatDate(data.DateAccrued),
          },
          {
            id: 'AccountType',
            header: t('AccountType.column', { defaultValue: 'Account Type' }),
            align: 'left',
            template: ({ data }) =>
              data.InterestMaturityType === 'short-term'
                ? tDrawer('standard', { defaultValue: 'Standard' })
                : tDrawer('premium', { defaultValue: 'Premium' }),
            visible: hasLongTermAccount,
          },
          {
            id: 'Rate',
            header: t('Rate.column', { defaultValue: 'Daily Interest Rate' }),
            align: 'left',
            field: 'Rate',
          },
          {
            id: 'AmountAccrued',
            header: t('AmountAccrued.column', { defaultValue: 'Interest Gained' }),
            align: 'left',
            template: ({ data }) => `${currency} ${formattedCurrency(data.AmountAccrued)}`,
          },
        ]}
      />

      {fetching ? (
        <div className={cn('spinner')}>
          <Icon icon="loader" size="xl-jumbo" />
        </div>
      ) : (
        interestAccruedDetails.length === 0 && (
          <Box flx="col" jc="center">
            <Box ai="center" jc="center" pt="lg">
              {t('noResults', { defaultValue: 'There are no records to display within this date range' })}
            </Box>
          </Box>
        )
      )}
    </div>
  );
};

export default InterestHistoryTable;
